import { memo, ReactNode, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { CookieConsentModal } from "./components/CookieConsentModal";
import { CookieScripts } from "./components/CookieScripts";
import {
    hasAnalyticsCookieConsent,
    hasCookieConsentDecision,
    syncAnalyticsConsentToServer,
    writeCookieConsentPrefs,
    type CookieConsentPrefs,
} from "./utils/analyticsConsent";
import { LEAGUE_ID_QUERY, LEAGUES_DEMO_PATH, leagueDetailPath, leagueJoinPath, readLeagueDetailIdFromSearch, readLeagueJoinCodeFromSearch } from "./components/Leagues/lib/leagueGamePlayUrl";
import { readLeagueResultsShareTokenFromSearch } from "./components/Leagues/lib/leagueResultsShare";
import { peekActiveLeagueId } from "./components/Leagues/lib/leagueModeStore";
import { TraderDataFetcherProvider } from "./Data/TraderDataFetcherContext";
import { DecimalSocketProvider } from "./Data/DecimalSocketContext";
import { UserProvider, useUser } from "./Data/UserContext";
import AuthenticatedRoute from "./components/AuthenticatedRoute";
import { PreferencesRedirect } from "./components/Account/PreferencesRedirect";
import ShopBtn from "./components/Shop/ShopBtn";
import Refund from "./components/Support/Refund";
import RefundPolicy from "./components/Support/RefundPolicy";
import { isAnalyticsPathExcluded, isDevEnv } from "./utils/analyticsEnv";
import { getAuthHeaders } from "./utils/authHeaders";
import { initializeGtm, seedAnalyticsIdentityBeforeGtm } from "./utils/gtm";
import { trackPageView } from "./utils/gtmEvents";
import { motion } from "framer-motion";
import { authPageRedirectTarget } from "./utils/authBackPath";
import { isEmailAllowedForDevGate } from "./utils/devEmailAccess";
import { setBfePending } from "./utils/welcomeBonus";
import { retryLazyImport } from "./utils/retryLazyImport";
import { useGamesEnabled } from "./hooks/useGamesEnabled";
import { usePlatformFeatureFlags } from "./hooks/usePlatformFeatureFlags";
import { AppLoadingSkeleton } from "./components/AppLoadingSkeleton";
import { ModalCloseIcon } from "./components/ModalCloseIcon";
import { buildMatchesListHref, matchesHubSportSearchParam } from "./components/Matches/matchesHubNavigation";
import { AdminDevProxyReload } from "./components/AdminDevProxyReload";

// Apply retry logic to all lazy-loaded components, especially critical ones like Games (homepage) and Layout
const NotFound = retryLazyImport(() => import("./components/404"));
const Account = retryLazyImport(() => import("./components/Account"));
const AboutUs = retryLazyImport(() => import("./components/AboutUs"));
const HowToPlay = retryLazyImport(() => import("./components/HowToPlay"));
const Compliance = retryLazyImport(() => import("./components/Compliance"));
const FacebookDataDeletionStatus = retryLazyImport(
    () => import("./components/Account/FacebookDataDeletionStatus")
);
const Contact = retryLazyImport(() => import("./components/Contact"));
const Games = retryLazyImport(() => import("./components/Games"));
const Layout = retryLazyImport(() => import("./components/Layout"));
const GoogleNewSignup = retryLazyImport(() => import("./components/Account/NewAccountGoogle"));
const OtpNewSignup = retryLazyImport(() => import("./components/Account/NewAccountOtp"));
const Privacy = retryLazyImport(() => import("./components/Privacy"));
const Signup = retryLazyImport(() => import("./components/Account/Signup"));
const Terms = retryLazyImport(() => import("./components/Terms"));
const Sitemap = retryLazyImport(() => import("./components/Sitemap"));
const Trader = retryLazyImport(() => import("./components/Trader/index"));
const SettlementRules = retryLazyImport(() => import("./components/Trader/SettlementRules"));
const Squares = retryLazyImport(() => import("./components/Squares"));
const Bullion = retryLazyImport(() => import("./components/Bullion"));
const Sixes = retryLazyImport(() => import("./components/Sixes"));

const ShopPaymentReturn = retryLazyImport(() => import("./components/Shop/ShopPaymentReturn"));
const ProfileRoute = retryLazyImport(() => import("./components/Profile/ProfileRoute"));
const ProfileEditRoute = retryLazyImport(() => import("./components/Profile/ProfileEditRoute"));
const MyZonePage = retryLazyImport(() => import("./components/MyZone"));
const XpLeaderboardPage = retryLazyImport(() => import("./components/Xp/XpLeaderboardPage"));
const Matches = retryLazyImport(() => import("./components/Matches"));
const LeaguesHub = retryLazyImport(() => import("./components/Leagues"));
const LeagueDetail = retryLazyImport(() => import("./components/Leagues/LeagueDetailRoute"));
const LeagueJoin = retryLazyImport(() => import("./components/Leagues/LeagueJoinRoute"));
const loadLeagueResultsShare = () => import("./components/Leagues/LeagueResultsShareRoute");
const LeagueResultsShare = retryLazyImport(loadLeagueResultsShare);
const LeaguesDemoProvider = retryLazyImport(() => import("./components/Leagues/demo/LeaguesDemoProvider"));
// Prefetch share chunk on hard-load `/leagues?r=` so Suspense resolves faster.
if (typeof window !== "undefined" && /(?:\?|&)r=/.test(window.location.search)) {
    void loadLeagueResultsShare();
}
const gtmId = process.env.GTM_ID;

const isShopEnabled = process.env.HIDE_SHOP ? process.env.HIDE_SHOP === "false" : true;

/** League in-mode play deep-links must reach the game even when it is hidden from the main nav. */
function useLeaguePlayRouteAllowed(): boolean {
    const [searchParams] = useSearchParams();
    return Boolean(searchParams.get(LEAGUE_ID_QUERY)?.trim() || peekActiveLeagueId());
}

/**
 * The single `/leagues` (and `/leaguesdemo`) entry point. Surfaces live on
 * one single-segment path — dispatched by query param — so nginx's SPA fallback serves
 * hard loads/refresh without any per-route rewrite:
 *   - `?id=`   → league detail
 *   - `?code=` → join landing
 *   - `?r=`    → finished-league results share card
 *   - neither  → hub
 */
function LeaguesHubJoinOrDetail() {
    const { isLeaguesEnvEnabled, loading: flagsLoading } = usePlatformFeatureFlags();
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();
    // Public results shares: render immediately (don't wait on feature flags).
    if (readLeagueResultsShareTokenFromSearch(search)) return <LeagueResultsShare />;
    if (flagsLoading) return <AppLoadingSkeleton />;
    if (!isLeaguesEnvEnabled) return <Navigate to="/" replace />;
    if (readLeagueDetailIdFromSearch(search)) return <LeagueDetail />;
    return readLeagueJoinCodeFromSearch(search) ? <LeagueJoin /> : <LeaguesHub />;
}

/** Old `/leagues/join/:code` → `/leagues?code=` (and demo equivalent). */
function LeagueJoinPathRedirect() {
    const { code } = useParams<{ code: string }>();
    const { pathname } = useLocation();
    const basePath = pathname.startsWith(LEAGUES_DEMO_PATH) ? LEAGUES_DEMO_PATH : "/leagues";
    return <Navigate to={leagueJoinPath(code ?? "", basePath)} replace />;
}

/** Old `/leagues/:leagueId` → `/leagues?id=` (and demo equivalent) so already-shared links keep working. */
function LeagueDetailPathRedirect() {
    const { leagueId } = useParams<{ leagueId: string }>();
    const { pathname } = useLocation();
    const basePath = pathname.startsWith(LEAGUES_DEMO_PATH) ? LEAGUES_DEMO_PATH : "/leagues";
    return <Navigate to={leagueDetailPath(leagueId ?? "", basePath)} replace />;
}

function BullionRoute() {
    const { isBullionVisible, loading } = useGamesEnabled();
    const leaguePlay = useLeaguePlayRouteAllowed();
    if (!loading && !isBullionVisible && !leaguePlay) {
        return <Navigate to="/" replace />;
    }
    return <Bullion />;
}

function SixesRoute() {
    const { isSixesVisible, loading } = useGamesEnabled();
    const leaguePlay = useLeaguePlayRouteAllowed();
    if (!loading && !isSixesVisible && !leaguePlay) {
        return <Navigate to="/" replace />;
    }
    return <Sixes />;
}

function SquaresRoute() {
    const { isSquaresVisible, loading } = useGamesEnabled();
    const leaguePlay = useLeaguePlayRouteAllowed();
    if (!loading && !isSquaresVisible && !leaguePlay) {
        return <Navigate to="/" replace />;
    }
    return <Squares />;
}

function TraderRoute() {
    const { isTraderVisible, loading } = useGamesEnabled();
    const leaguePlay = useLeaguePlayRouteAllowed();
    if (!loading && !isTraderVisible && !leaguePlay) {
        return <Navigate to="/" replace />;
    }
    return <Trader />;
}

const EMAIL_LINK_PARAM = "bfe";

const LayoutWithConsent = memo(({ children }: { children: ReactNode }) => {
    const [hasAccepted, setHasAccepted] = useState(() => hasAnalyticsCookieConsent());
    const [showConsentModal, setShowConsentModal] = useState(() => !hasCookieConsentDecision());
    const [showEmailLinkBonusAlready, setShowEmailLinkBonusAlready] = useState(false);
    const [showBfeLoggedOutModal, setShowBfeLoggedOutModal] = useState(true);
    const emailLinkBonusClaimAttempted = useRef(false);
    const location = useLocation();
    const { user, loading, setUser } = useUser();

    const handleCookieDecision = (prefs: CookieConsentPrefs) => {
        writeCookieConsentPrefs(prefs);
        setHasAccepted(prefs.analytics === true);
        setShowConsentModal(false);
        syncAnalyticsConsentToServer(prefs.analytics === true);
    };

    // Dev environment access gate - restrict to allowed email domain when configured
    const envValue = useMemo(() => (process.env.ENV || process.env.ENVIRONMENT || "").toLowerCase(), []);
    const isDevByEnvVar = useMemo(() => envValue === "dev" || envValue === "development", [envValue]);
    const allowedDomain = useMemo(() => (process.env.ALLOWED_EMAIL_DOMAIN || "").toLowerCase(), []);
    const allowedEmailsRaw = useMemo(() => process.env.ALLOWED_EMAILS, []);
    const restrictionActive = useMemo(() => isDevByEnvVar && !!allowedDomain, [isDevByEnvVar, allowedDomain]);
    const currentPath = location.pathname || "/";
    const userEmailAllowed = useMemo(
        () => isEmailAllowedForDevGate(user?.email, allowedDomain, allowedEmailsRaw),
        [user?.email, allowedDomain, allowedEmailsRaw],
    );
    const onAuthPages = useMemo(() => new Set<string>(["/login", "/signup"]).has(currentPath), [currentPath]);
    // Routes that require authentication (logged-in users only)
    const authenticatedRoutes = useMemo(() => new Set<string>(["/shop", "/refund", "/refundpolicy"]), []);
    const isPublicPage = useMemo(() => {
        // Check if current path is an auth page (login/signup)
        if (onAuthPages) return true;
        // Check if current path requires authentication
        if (authenticatedRoutes.has(currentPath)) return false;
        // Check if current path starts with an authenticated route prefix
        for (const route of authenticatedRoutes) {
            if (currentPath.startsWith(route + "/")) return false;
        }
        // All other routes are public
        return true;
    }, [currentPath, authenticatedRoutes, onAuthPages]);

    useEffect(() => {
        if (hasAccepted && gtmId && !isDevEnv() && !isAnalyticsPathExcluded(location.pathname)) {
            if (user?.userId) {
                trackPageView({
                    user_id: user.userId,
                    page_path: location.pathname,
                });
            } else {
                trackPageView({
                    user_id: "VISITOR",
                    page_path: location.pathname,
                });
            }
        }
    }, [location.pathname, hasAccepted, user?.userId]);

    useEffect(() => {
        if (hasAccepted && gtmId && !isDevEnv() && !isAnalyticsPathExcluded(location.pathname)) {
            // Sticky user_status must exist on dataLayer before GTM/GA4 native hits.
            seedAnalyticsIdentityBeforeGtm(user?.userId ?? null);
            initializeGtm({ gtmId, events: { userdata: "userdata" } });
        }
        // Re-check when leaving excluded paths (e.g. /match → /matches) so GTM can start.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- identity updates handled elsewhere
    }, [hasAccepted, location.pathname]);

    // When user has bfe=1 in URL but is not logged in, remember so we can add bfe=1 to redirect after login.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!loading && !user && params.get(EMAIL_LINK_PARAM) === "1") {
            setBfePending();
        }
    }, [location.search, user, loading]);

    // One-time welcome bonus when user opens site from email link (?bfe=1). Claim only once per account (backend enforces).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fromEmail = params.get(EMAIL_LINK_PARAM);
        if (loading || !user || fromEmail !== "1" || emailLinkBonusClaimAttempted.current) return;
        emailLinkBonusClaimAttempted.current = true;
        (async () => {
            try {
                const apiUrl = process.env.API_URL || "";
                const res = await fetch(`${apiUrl}/welcome-bonus/claim`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                });
                const data = await res.json().catch(() => ({}));
                params.delete(EMAIL_LINK_PARAM);
                const newSearch = params.toString();
                const newPath = location.pathname + (newSearch ? `?${newSearch}` : "");
                window.history.replaceState({}, "", newPath);
                if (data.applied === true) {
                    try {
                        sessionStorage.setItem("c8_check_welcome_notification", "1");
                        sessionStorage.setItem("c8_animate_wallet_on_open", "1");
                    } catch {
                        /* ignore */
                    }
                    if (data.wallet && user) {
                        setUser({ ...user, wallet: data.wallet });
                        window.dispatchEvent(new CustomEvent("walletUpdate"));
                    }
                    window.dispatchEvent(new CustomEvent("c8-notifications-updated"));
                } else if (data.alreadyClaimed === true) {
                    setShowEmailLinkBonusAlready(true);
                }
            } catch {
                params.delete(EMAIL_LINK_PARAM);
                const newSearch = params.toString();
                window.history.replaceState({}, "", location.pathname + (newSearch ? `?${newSearch}` : ""));
            }
        })();
    }, [location.search, location.pathname, user, loading, setUser]);

    // Session check does not block the homepage
    if (restrictionActive) {
        if (loading) {
            return null;
        }
        // Allow public pages, auth pages, and homepage for logged-out users
        if (!user && !onAuthPages && !isPublicPage) {
            const bfe = new URLSearchParams(location.search).get(EMAIL_LINK_PARAM) === "1" ? "?bfe=1" : "";
            return <Navigate to={`/login${bfe}`} replace />;
        }
        // Phone/SMS accounts have no email — don't treat empty domain as a mismatch.
        if (user && !userEmailAllowed) {
            return <div className="fixed inset-0 flex items-center justify-center font-anton text-lime bg-main text-[24px] px-6 text-center">Access restricted on dev. Only {allowedDomain} accounts are allowed.</div>;
        }
    }

    if (onAuthPages && loading) {
        return <div className="app-loading-shell" aria-hidden="true" />;
    }

    if (user && !user.is_guest && onAuthPages) {
        return <Navigate to={authPageRedirectTarget(location.search)} replace />;
    }

    const hasBfeParam = !loading && new URLSearchParams(location.search).get(EMAIL_LINK_PARAM) === "1";
    const showLoggedOutBfeModal = hasBfeParam && !user && showBfeLoggedOutModal && !onAuthPages;

    const closeEmailBonusAlreadyModal = () => setShowEmailLinkBonusAlready(false);
    const modalCloseButtonClass = "absolute top-2 right-2 min-h-11 min-w-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold text-lg leading-none transition-colors";

    return (
        <>
            {showEmailLinkBonusAlready && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60">
                    <motion.div
                        className="relative border-2 border-[#D2FF00] bg-[#2a1a3d] shadow-xl p-6 md:p-8 max-w-md w-full"
                        initial={{ opacity: 0, scale: 0.85, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ type: "spring", stiffness: 280, damping: 22 }}
                    >
                        <button type="button" onClick={closeEmailBonusAlreadyModal} className={modalCloseButtonClass} aria-label="Close">
                            <ModalCloseIcon width={28} height={28} fill="currentColor" className="shrink-0" />
                        </button>
                        <p className="text-white font-chakra text-[14px] md:text-[16px] normal-case pr-10">
                            You&apos;ve already received your welcome bonus. Enjoy the site!
                        </p>
                        <div className="flex flex-col gap-3 mt-6">
                            <button type="button" onClick={closeEmailBonusAlreadyModal} className="w-full py-3 bg-[#D2FF00] text-[#1B062F] font-anton text-[16px] uppercase hover:bg-[#B8E600] transition-colors">Got it</button>
                        </div>
                    </motion.div>
                </div>
            )}
            {showLoggedOutBfeModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60">
                    <motion.div
                        className="relative border-2 border-[#D2FF00] bg-[#2a1a3d] shadow-xl p-6 md:p-8 max-w-md w-full"
                        initial={{ opacity: 0, scale: 0.85, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ type: "spring", stiffness: 280, damping: 22 }}
                    >
                        <button type="button" onClick={() => setShowBfeLoggedOutModal(false)} className={modalCloseButtonClass} aria-label="Close">
                            <ModalCloseIcon width={28} height={28} fill="currentColor" className="shrink-0" />
                        </button>
                        <span className="text-2xl" aria-hidden>
                            🎉
                        </span>
                        <h2 className="text-[#D2FF00] font-anton text-[18px] md:text-[22px] uppercase mb-2 pr-8">
                            Welcome bonus
                        </h2>
                        <p className="text-white font-chakra text-[14px] md:text-[16px] normal-case mb-6">
                            Login or sign up to claim <strong>1 Ruby</strong> and <strong>1000 Gold Balls</strong> added to your wallet.
                        </p>
                        <div className="flex justify-center">
                            <a href="/login?bfe=1" className="px-6 py-3 bg-[#D2FF00] text-[#1B062F] font-anton text-[16px] uppercase hover:bg-[#B8E600] transition-colors inline-block">
                                Login to claim
                            </a>
                        </div>
                    </motion.div>
                </div>
            )}
            <Layout>
                {children}
                {showConsentModal ? <CookieConsentModal onDecision={handleCookieDecision} /> : null}
            </Layout>
            {hasAccepted && <CookieScripts />}
        </>
    );
});

const LegacyMatchParamRedirect = () => {
    const { match_id } = useParams();
    const destination = match_id
        ? `/matches?id=${encodeURIComponent(match_id)}&${matchesHubSportSearchParam("CRICKET")}`
        : buildMatchesListHref();
    return <Navigate to={destination} replace />;
};

const LegacyMatchPathRedirect = () => {
    const location = useLocation();
    return <Navigate to={`/matches${location.search}${location.hash}`} replace />;
};

// Build routes array with conditional shop route
const routes = [
    // ========== PUBLIC ROUTES ==========
    {
        path: "/",
        element: (
            <LayoutWithConsent>
                <Games />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/all-games",
        element: (
            <LayoutWithConsent>
                <Games />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/about",
        element: (
            <LayoutWithConsent>
                <AboutUs />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/how-to-play",
        element: (
            <LayoutWithConsent>
                <HowToPlay />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/compliance",
        element: (
            <LayoutWithConsent>
                <Compliance />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/facebook-data-deletion",
        element: (
            <LayoutWithConsent>
                <FacebookDataDeletionStatus />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/account_google_newsignup",
        element: (
            <LayoutWithConsent>
                <GoogleNewSignup />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/account_facebook_newsignup",
        element: (
            <LayoutWithConsent>
                <GoogleNewSignup />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/account_otp_newsignup",
        element: (
            <LayoutWithConsent>
                <OtpNewSignup />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/matches",
        element: (
            <LayoutWithConsent>
                <Matches />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/match",
        element: <LegacyMatchPathRedirect />,
    },
    {
        path: "/match/:match_id",
        element: (
            <LayoutWithConsent>
                <LegacyMatchParamRedirect />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/matchdetail",
        element: <LegacyMatchPathRedirect />,
    },
    {
        path: "/login",
        element: (
            <LayoutWithConsent>
                <Signup />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/signup",
        element: (
            <LayoutWithConsent>
                <Signup />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/terms",
        element: (
            <LayoutWithConsent>
                <Terms />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/privacy",
        element: (
            <LayoutWithConsent>
                <Privacy />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/contact",
        element: (
            <LayoutWithConsent>
                <Contact />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/support",
        element: (
            <Navigate to="/about" replace />
        ),
    },
    {
        path: "/settlement-rules",
        element: (
            <LayoutWithConsent>
                <SettlementRules />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/sitemap",
        element: (
            <LayoutWithConsent>
                <Sitemap />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/trader",
        element: (
            <LayoutWithConsent>
                <TraderRoute />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/results",
        element: <LegacyMatchPathRedirect />,
    },
    {
        path: "/squares",
        element: (
            <LayoutWithConsent>
                <SquaresRoute />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/bullion",
        element: (
            <LayoutWithConsent>
                <BullionRoute />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/sixes",
        element: (
            <LayoutWithConsent>
                <SixesRoute />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/my-games",
        element: <Navigate to="/my-zone?tab=my-games" replace />,
    },
    // ========== LEAGUES (real API data) ==========
    {
        path: "/leagues",
        element: (
            <LayoutWithConsent>
                <LeaguesHubJoinOrDetail />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/leagues/join/:code",
        element: <LeagueJoinPathRedirect />,
    },
    // ========== LEAGUES DEMO (always-on client-side prototype) ==========
    // Nested under a provider that swaps the data source to the prototype.
    // Single-segment path so nginx SPA fallback serves a hard load.
    {
        path: LEAGUES_DEMO_PATH,
        element: <LeaguesDemoProvider />,
        children: [
            {
                index: true,
                element: (
                    <LayoutWithConsent>
                        <LeaguesHubJoinOrDetail />
                    </LayoutWithConsent>
                ),
            },
            {
                path: "join/:code",
                element: <LeagueJoinPathRedirect />,
            },
            {
                path: ":leagueId",
                element: <LeagueDetailPathRedirect />,
            },
        ],
    },
    {
        path: "/leagues/:leagueId",
        element: <LeagueDetailPathRedirect />,
    },
    // ========== AUTHENTICATED ROUTES (Require Login) ==========
    {
        path: "/edit-profile",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <ProfileEditRoute />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/profile",
        element: (
            <LayoutWithConsent>
                <ProfileRoute />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/xp-leaderboard",
        element: (
            <LayoutWithConsent>
                <XpLeaderboardPage />
            </LayoutWithConsent>
        ),
    },
    {
        path: "/my-zone",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <MyZonePage />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/account",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <Account />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/opt-in",
        element: (
            <LayoutWithConsent>
                <PreferencesRedirect />
            </LayoutWithConsent>
        ),
    },
    ...(isShopEnabled
        ? [
              {
                  path: "/shop",
                  element: (
                      <AuthenticatedRoute>
                          <LayoutWithConsent>
                              <ShopBtn />
                          </LayoutWithConsent>
                      </AuthenticatedRoute>
                  ),
              },
              {
                  path: "/shop-payment-return",
                  element: (
                      <AuthenticatedRoute>
                          <LayoutWithConsent>
                              <ShopPaymentReturn />
                          </LayoutWithConsent>
                      </AuthenticatedRoute>
                  ),
              },
          ]
        : []),
    // Always register so Stripe redirect after payment is handled (server must serve index.html for this path)
    {
        path: "/shop-payment-return",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <ShopPaymentReturn />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/refund",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <Refund />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/refund/:id",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <Refund />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/refundpolicy",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <RefundPolicy />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    {
        path: "/refundpolicy/:id",
        element: (
            <AuthenticatedRoute>
                <LayoutWithConsent>
                    <RefundPolicy />
                </LayoutWithConsent>
            </AuthenticatedRoute>
        ),
    },
    ...(process.env.NODE_ENV !== "production"
        ? [
              { path: "/admin", element: <AdminDevProxyReload /> },
              { path: "/admin/*", element: <AdminDevProxyReload /> },
          ]
        : []),
    {
        path: "*",
        element: (
            <LayoutWithConsent>
                <NotFound />
            </LayoutWithConsent>
        ),
    },
];

const router = createBrowserRouter(routes);

export const App = () => {
    return (
        <Suspense
            fallback={
                /* Matches index.html .app-loading-skeleton so there’s no flash when React takes over */
                <AppLoadingSkeleton />
            }
        >
            <UserProvider>
                <DecimalSocketProvider>
                    <TraderDataFetcherProvider>
                        <RouterProvider router={router} />
                    </TraderDataFetcherProvider>
                </DecimalSocketProvider>
            </UserProvider>
        </Suspense>
    );
};
